import { FaSolidArrowLeft, FaSolidTrash } from 'solid-icons/fa'
import { For, Show, createSignal } from 'solid-js'
import type { MainMenuObj } from '../../../../components/ModalMenu/MainMenuObj.type'
import { setModalMenu } from '../../../../rx/shared/ModalMenu/ModalMenuSignal'
import { emitEvent } from '../../../event/event'
import Button from '../../../jsx/Button'
import type { ActionBinding } from '../ActionBinding.type'
import KeybindingEditor from '../Components/KeybindingEditor'
import { InputActionType } from '../InputActionType'
import inputActionTypeMetaList from '../inputActionTypeMetaList'
import { getInputProfile, inputProfileUpdatedEvent, setInputProfile } from '../InputProfile/inputProfileSignal'
import InputProfileListPage from './InputProfileListPage'

const InputProfileEditorPage: MainMenuObj = {
  dname: 'Edit Keybindings',
  parents: () => [InputProfileListPage],
  // fullscreen: true,
  fullscreen: 'md-down',
  size: 'xl',
  fn() {
    const profile = getInputProfile
    const [editingAction, setEditingAction] = createSignal<InputActionType | null>(null)

    const handleSave = () => {
      const currentProfile = profile()
      const updatedProfile = {
        ...currentProfile,
        updated_at: Date.now(),
      }
      setInputProfile(updatedProfile)
      emitEvent(inputProfileUpdatedEvent, updatedProfile)
    }

    const handleBlankAction = (action: InputActionType) => {
      const currentProfile = profile()
      const updatedBindings = currentProfile.actionBindings.filter(ab => ab.action !== action)
      const updatedProfile = {
        ...currentProfile,
        actionBindings: updatedBindings,
        updated_at: Date.now(),
      }
      setInputProfile(updatedProfile)
      emitEvent(inputProfileUpdatedEvent, updatedProfile)
    }

    const getActionBindings = (action: InputActionType): ActionBinding[] => {
      return profile().actionBindings.filter(ab => ab.action === action)
    }

    return (
      <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <Button
            class="btn btn-sm btn-outline-secondary"
            onClick={() => setModalMenu(InputProfileListPage)}
          >
            <FaSolidArrowLeft /> Back
          </Button>
          <Button
            class="btn btn-primary"
            onClick={handleSave}
          >
            Save
          </Button>
        </div>

        <table class="table table-striped">
          <thead>
            <tr>
              <th>Action</th>
              <th>Bindings</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <For each={inputActionTypeMetaList}>
              {(inputActionTypeMeta) => {
                const { id: action, dname } = inputActionTypeMeta
                const bindings = () => getActionBindings(action)
                return (
                  <tr>
                    <td>{dname}</td>
                    <td>
                      <Show when={editingAction() === action}>
                        <KeybindingEditor
                          action={action}
                          existingBindings={bindings()}
                          onSave={(newBindings) => {
                            const currentProfile = profile()
                            // Remove old bindings for this action
                            const otherBindings = currentProfile.actionBindings.filter(ab => ab.action !== action)
                            // Add new bindings
                            const updatedBindings = [...otherBindings, ...newBindings]
                            const updatedProfile = {
                              ...currentProfile,
                              actionBindings: updatedBindings,
                              updated_at: Date.now(),
                            }
                            setInputProfile(updatedProfile)
                            emitEvent(inputProfileUpdatedEvent, updatedProfile)
                            setEditingAction(null)
                          }}
                          onCancel={() => setEditingAction(null)}
                        />
                      </Show>
                      <Show when={editingAction() !== action}>
                        <div>
                          <Show when={bindings().length === 0}>
                            <span class="text-muted">No bindings</span>
                          </Show>
                          <For each={bindings()}>
                            {(binding) => (
                              <div class="badge bg-secondary me-1">
                                {formatBinding(binding.input)}
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>
                    </td>
                    <td>
                      <Show when={editingAction() !== action}>
                        <Button
                          class="btn btn-sm btn-primary me-2"
                          onClick={() => setEditingAction(action)}
                        >
                          Edit
                        </Button>
                        <Button
                          class="btn btn-sm btn-danger"
                          onClick={() => handleBlankAction(action)}
                          disabled={bindings().length === 0}
                        >
                          <FaSolidTrash />
                        </Button>
                      </Show>
                    </td>
                  </tr>
                )
              }}
            </For>
          </tbody>
        </table>
      </div>
    )
  }
}

function formatBinding(input: ActionBinding['input']): string {
  if (input.device === 0) { // Keyboard
    const parts: string[] = []
    if (input.ctrl) parts.push('Ctrl')
    if (input.alt) parts.push('Alt')
    if (input.shift) parts.push('Shift')
    parts.push(formatKeyCode(input.code))
    return parts.join(' + ')
  } else if (input.device === 1) { // Mouse
    const buttonNames = ['Left', 'Middle', 'Right', 'Back', 'Forward']
    return `Mouse ${buttonNames[input.button] || `Button ${input.button}`}`
  } else if (input.device === 2) { // Gamepad
    return `Gamepad Button ${input.button}`
  } else if (input.device === 3) { // Touch
    return 'Touch'
  }
  return 'Unknown'
}

function formatKeyCode(code: string): string {
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)
  if (code === 'Space') return 'Space'
  if (code === 'Enter') return 'Enter'
  if (code === 'Escape') return 'Esc'
  if (code === 'ArrowUp') return '↑'
  if (code === 'ArrowDown') return '↓'
  if (code === 'ArrowLeft') return '←'
  if (code === 'ArrowRight') return '→'
  return code
}

export default InputProfileEditorPage

